import * as Yup from 'yup';
import React, { useState, useMemo } from 'react';
import { Button } from 'components/shadcn/ui/button';
import { Dialog, DialogContent } from 'components/shadcn/ui/dialog';
import { Card, CardContent } from 'components/shadcn/ui/card';
import { useToast } from 'components/shadcn/hooks/use-toast';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { logActivity, useJwtToken } from 'helper';
import { inviteUser } from '../service';
import { Formik, Form } from 'formik';
import getValidationSchema from 'components/HGForm/formValidations';
import { addSpaceBeforeEveryCapitalLetter } from 'components/HGForm/helpers';
import HGFormInput from 'components/HGForm/HGFormInput';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import HGError from 'components/HGForm/HGError';
import HGSuccessCard from 'components/HGSuccessCard';

const InviteCustomerModal = ({ open, toggle }) => {
    const accessToken = useJwtToken();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const initialFieldsValues = useMemo(
        () => ({
            customerEmail: '',
        }),
        []
    );
    const fieldKeysArray = Object.keys(initialFieldsValues);

    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);
            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const onClose = () => {
        setSuccess(false);
        setErrorMessage(null);
        toggle(false);
    };

    const handleCopyClick = () => {
        const textToCopy = `
            Dear Homegrid Customer,

            You have been invited to use our app as a customer.
            Please use the link below to sign up for a customer account.

            https://app.homegridenergy.com

            Thank you,
            Lithion Battery
        `;
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => toast({ title: 'Invite email Copied' }))
            .catch((err) => console.error('Failed to copy text: ', err));
    };

    const sendInvitationEmail = async (values) => {
        setIsLoading(true);
        const { customerEmail } = values;
        if (customerEmail) {
            logActivity(
                'User',
                'Info',
                'Invite Customer',
                `Invite email sent to customer: ${customerEmail}`,
                {
                    before: values,
                    after: null,
                }
            );
            try {
                await inviteUser(
                    {
                        CustomerEmailAddress: customerEmail,
                        UserType: 'Customer',
                    },
                    accessToken
                );
                setSuccess(true);
            } catch (error) {
                setErrorMessage(error.message || 'Invite failed');
                console.error('Error sending invite email to customer:', error);
            }
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={() => toggle(!open)}>
            <DialogContent className="max-w-[500px] p-0">
                {success ? (
                    <HGSuccessCard
                        buttons={[
                            {
                                onClick: () => onClose(),
                                buttonText: 'Close',
                            },
                        ]}
                    >
                        The invitation email has been sent successfully!
                    </HGSuccessCard>
                ) : (
                    <Card>
                        <CardContent className="p-3">
                            <h4 className="text-2xl font-semibold mb-3 text-center">
                                Invite Customer
                            </h4>
                            <Formik
                                initialValues={initialFieldsValues}
                                validationSchema={validationSchema}
                                onSubmit={sendInvitationEmail}
                                validateOnBlur={false}
                            >
                                {() => (
                                    <Form>
                                        <div className="gap-2 flex flex-col sm:flex-row justify-center mb-3">
                                            <HGFormInput
                                                name="customerEmail"
                                                type="email"
                                                label="Customer Email"
                                                variant="outlined"
                                                placeholder="Please enter customer email"
                                                fullWidth
                                            />
                                        </div>
                                        <p className="text-sm mb-3">
                                            This is a preview of the customer
                                            invitation email. Click send to send
                                            it directly from here or click to
                                            copy the message and send it from a
                                            separate email.
                                        </p>
                                        <div className="border border-border rounded p-2 mb-3 bg-muted relative">
                                            <p className="text-sm">
                                                Dear Homegrid Customer,
                                                <br />
                                                <br />
                                                You have been invited to use our
                                                app as a customer. Please use
                                                the link below to sign up for a
                                                customer account.
                                                <br />
                                                <br />
                                                <a href="https://app.homegridenergy.com">
                                                    https://app.homegridenergy.com
                                                </a>
                                                <br />
                                                <br />
                                                Thank you,
                                                <br />
                                                Lithion Battery
                                            </p>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="absolute right-2.5 top-2.5"
                                                onClick={handleCopyClick}
                                            >
                                                <ClipboardDocumentIcon className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        {errorMessage && (
                                            <HGError
                                                errorMessage={errorMessage}
                                                sx={{ mb: 3 }}
                                            />
                                        )}
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Button
                                                variant="secondary"
                                                className="w-full"
                                                onClick={onClose}
                                            >
                                                Close
                                            </Button>
                                            <HGFormSubmitBtn
                                                btnText="Send"
                                                loading={isLoading}
                                                sx={{ width: '100%' }}
                                            />
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </CardContent>
                    </Card>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InviteCustomerModal;
